import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ModalCloseIcon, TickIcon, TickSVG, TickSVGBlack } from './Icons';
import { truncateField } from '@src/helpers/Helpers';
import { IsMobileWidth } from './MediaQueryUtils';
import { RESTAURANT } from '@src/Constants';
import {
	Box,
	CircularProgress,
	Container,
	Divider,
	Stack,
	Typography,
	TextareaAutosize,
} from '@mui/material';
import CustomButton from './CustomButton/CustomButton';
import CustomModal from './CustomModal';
import LabeledSelectField from './CustomSelect/LabeledSelectField';
import LabeledTextField from './CustomTextField/LabeledTextField';
import moment from 'moment';
import CustomDateTimePicker from './CustomDateTimePicker/CustomDateTimePicker';

interface Step {
	number: number;
	changes: string[];
	title: { name: string; date: string; time: string };
	active?: boolean;
	user_type: string;
}

interface CustomStepperProps {
	steps: Step[];
	style?: React.CSSProperties;
}

const StepContainer = styled.div<{ step: Step }>`
	position: relative;
	height: ${(props) => props.step?.changes?.length * 30 + 72}px;
	color: grey;
	> div:first-child {
		position: static;
		height: 0;
	}
	#line {
		margin-left: 1.5em;
		padding-left: 1em;
		position: relative;
		&::before {
			content: '';
			position: absolute;
			left: -17.5px; /* Adjust this value to control the position of the line */
			width: 2px;
			top: -9px;
			height: ${(props) => props.step?.changes?.length * 30 + 58}px;
			background-color: #f1f1f1;
		}
	}
	&.step-active {
		color: #4285f4;
	}
	&.step-active .circle {
		background-color: #0069b4;
	}
`;

const ChangesList = styled.ul`
	color: #7d7d7d;
	font-size: 14px;
	font-weight: 600;
	font-size: 0.8em;
	list-style-type: disc;
	padding-left: 1em;
	margin-top: 0.5em;
`;

const ChangeItem = styled.li`
	margin-bottom: 0.25em;
`;

const StepperTitle = styled.div`
	display: flex;
	flex-direction: column;
	position: relative;
	letter-spacing: 0.28px;
	#type {
		color: var(--Gray-g-600, #7d7d7d);
		font-size: 12px;
		font-weight: 700;
		margin-left: 3.1em;
		position: absolute;
		bottom: 26px;
	}
	#log {
		display: flex;
		align-items: center;
		gap: 20px;
		margin-left: 2.7em;
		margin-top: 3px;
		color: #333;
		font-size: 14px;
		font-weight: 700;
		#date {
			font-weight: 700;
		}
		#time {
			color: #acacac;
			font-size: 12px;
			font-weight: 600;
		}
	}
`;

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	letter-spacing: 0.28px;
	width: 100%;
	padding: 16px;
	gap: 14px;
`;

const StyledStack = styled(Stack)`
	.payement-labels {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
	}
	@media screen and (max-width: 600px) {
		.payement-label {
			font-size: 12px;
		}
		.payment-status {
			background-color: #0069b414;
			padding: 10px;
			border-radius: 12px;
			align-items: center;
			margin-bottom: 12px;
		}
		.payement-labels {
			align-items: flex-start;
		}
	}
`;

function AddPaymentStatusModal({
	showModal,
	setShowModal,
	capturePaymentDetails,
	paymentOptions,
	addPaymentStatusLoading,
	totalPayableAmount,
	totalPendingAmount,
}: any) {
	const options = paymentOptions?.paymentModes || [];
	const [amountError, setAmountError] = useState(false);
	const [paymentData, setPaymentData] = useState({
		paymentModeId: '',
		receivedAmount: '',
		receivedOn: new Date(),
		remarks: '',
	});

	function handleSubmit() {
		const regex = /^[+]?\d+(\.\d+)?$/;
		if (regex.test(paymentData.receivedAmount)) {
			setAmountError(false);
			capturePaymentDetails(paymentData);
		} else {
			setAmountError(true);
		}
	}

	return (
		<CustomModal
			open={showModal}
			setOpen={setShowModal}
			header={false}
			maxHeight={550}
			style={{ maxHeight: 550 }}
		>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				px={2}
				width="100%"
				height="56px"
			>
				<Typography variant="body1">Add Payment Status</Typography>
				<Box sx={{ cursor: 'pointer' }} onClick={() => setShowModal(false)}>
					<ModalCloseIcon />
				</Box>
			</Box>
			<Divider />
			<StyledContainer>
				<Stack mb={1} width={'100%'}>
					<Stack direction="row" justifyContent="space-between">
						<Typography fontSize={13}>Total Payable Amount: </Typography>
						<Typography fontSize={13} fontWeight={'bold'}>
							₹ {totalPayableAmount}
						</Typography>
					</Stack>
					<Stack direction="row" justifyContent="space-between">
						<Typography fontSize={13}>Total Pending Amount: </Typography>
						<Typography fontSize={13} fontWeight={'bold'}>
							₹ {totalPendingAmount}
						</Typography>
					</Stack>
				</Stack>
				<div style={{ width: '100%' }}>
					<LabeledSelectField
						required
						label="Payment Mode"
						value={paymentData?.paymentModeId}
						options={options || []}
						handleChange={(e: any) =>
							setPaymentData({
								...paymentData,
								paymentModeId: e?.target?.value as string,
							})
						}
						placeholder="eg. UPI"
						styles={{ width: '100%', height: '36px' }}
						disablePortal={false}
					/>
				</div>
				<div style={{ width: '100%' }}>
					<LabeledTextField
						required
						label="Received Amount"
						value={paymentData?.receivedAmount}
						onChange={(e: any) => {
							const value = e?.target?.value;
							// Regex to allow only positive numbers (including zero and fraction)
							const regex = /\d*(\.)?\d*/;
							if (regex.test(value)) {
								setAmountError(false);
								setPaymentData({
									...paymentData,
									receivedAmount: value,
								});
							}
						}}
						placeholder="eg. 5000"
						styles={{ width: '100%', height: '36px' }}
						error={amountError}
						errorText={(amountError && 'Please enter a valid amount') || ''}
					/>
				</div>
				<div style={{ width: '100%' }}>
					<CustomDateTimePicker
						label="Select Date and Time"
						styles={{ width: '328px', height: '36px' }}
						selectedDateTime={paymentData.receivedOn}
						onDateTimeChange={(dateTime) => {
							setPaymentData({
								...paymentData,
								receivedOn: dateTime || new Date(),
							});
						}}
						maxDateTime={new Date()}
					/>
				</div>

				<div style={{ width: '100%' }}>
					<TextareaAutosize
						maxRows={4}
						value={paymentData?.remarks}
						onChange={(e) =>
							setPaymentData({
								...paymentData,
								remarks: e?.target?.value,
							})
						}
						style={{
							resize: 'none',
							height: 100,
							border: '1.5px solid rgb(233, 233, 233)',
							borderRadius: 4,
							width: '100%',
							padding: '10px',
						}}
						placeholder="Remarks"
						maxLength={200}
					/>
				</div>

				<div
					style={{
						display: 'flex',
						alignItems: 'flex-end',
						justifyContent: 'flex-end',
						width: '100%',
					}}
				>
					<CustomButton
						title={
							addPaymentStatusLoading ? (
								<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
							) : (
								'Add'
							)
						}
						handleClick={() => handleSubmit()}
						heightVariant="medium"
						color="primary"
						styles={{
							height: '36px',
							padding: '7px 12px',
							width: '100%',
						}}
						disabled={
							!paymentData?.paymentModeId ||
							!paymentData?.receivedAmount ||
							addPaymentStatusLoading ||
							!paymentData?.receivedOn
						}
					/>
				</div>
			</StyledContainer>
		</CustomModal>
	);
}

function roundTo2Decimals(num: number) {
	return Math.round(num * 100) / 100;
}

function PaymentStatus({
	paymentHistory,
	capturePaymentDetails,
	paymentOptions,
	addPaymentStatusLoading,
	addPaymentStatusError,
	orderDetails,
	setAddPaymentStatusError,
}: any) {
	const isMobileWidth = IsMobileWidth();
	const [showModal, setShowModal] = useState(false);

	const orderTotal = orderDetails?.total;
	const discount = roundTo2Decimals(orderDetails?.rewardsDiscount || 0);
	const totalPayableAmount = roundTo2Decimals(orderTotal - discount);
	const totalPaidAmount = useMemo(() => {
		const total = paymentHistory?.reduce(
			(acc, curr) => acc + (parseFloat(curr?.received_amount) || 0),
			0
		);
		return roundTo2Decimals(total);
	}, [paymentHistory]);

	const totalPendingAmount = roundTo2Decimals(totalPayableAmount - totalPaidAmount);

	useEffect(() => {
		if (!addPaymentStatusError) {
			setShowModal(false);
			setAddPaymentStatusError(null);
		}
	}, [addPaymentStatusError]);

	return (
		<StyledStack
			direction={isMobileWidth ? 'column-reverse' : 'row'}
			p={3}
			justifyContent={'space-between'}
		>
			<div style={{ width: isMobileWidth ? '100%' : '60%' }}>
				{paymentHistory?.map((step, index) => {
					const { received_on, payment_mode, received_amount } = step || {};
					const { name } = payment_mode || {};
					const date = moment(received_on).format('YYYY-MM-DD');
					const time = moment(received_on).format('hh:mm A');
					const paymentText = `Paid Rs.${received_amount} via ${name}`;
					const remarks = step?.remarks ? `Remarks: ${step?.remarks}` : null;
					return (
						<StepContainer step={step} key={index}>
							<div style={{ zIndex: 40 }}>{<TickSVG />}</div>
							<StepperTitle>
								<div id="log">
									<div id="date">{date}</div>
									<div id="time">{time}</div>
								</div>
							</StepperTitle>
							<div id="line">
								<ChangesList>
									<ChangeItem dangerouslySetInnerHTML={{ __html: paymentText }} />
									{remarks && <ChangeItem dangerouslySetInnerHTML={{ __html: remarks }} />}
								</ChangesList>
							</div>
						</StepContainer>
					);
				})}
			</div>

			<Stack
				gap={1}
				direction={isMobileWidth ? 'row-reverse' : 'column'}
				alignItems={'flex-end'}
				justifyContent={isMobileWidth ? 'space-between' : 'unset'}
				className="payment-status"
			>
				<CustomButton
					title={isMobileWidth ? '+' : 'Add Payment Status'}
					color="primary"
					styles={{ height: '36px' }}
					handleClick={() => setShowModal(true)}
					disabled={totalPayableAmount === totalPaidAmount}
				/>
				<div className="payement-labels">
					<span className="payement-label" style={{ fontWeight: 'bold' }}>
						Total Payable Amount: ₹ {totalPayableAmount || 0}
					</span>
					<span className="payement-label" style={{ fontWeight: 'bold' }}>
						Amount Paid: ₹ {totalPaidAmount || 0}
					</span>
					<span className="payement-label" style={{ fontWeight: 'bold' }}>
						Amount Remaining: ₹ {totalPendingAmount || 0}
					</span>
				</div>
			</Stack>

			{showModal && (
				<AddPaymentStatusModal
					showModal={showModal}
					setShowModal={setShowModal}
					paymentOptions={paymentOptions}
					capturePaymentDetails={capturePaymentDetails}
					addPaymentStatusLoading={addPaymentStatusLoading}
					addPaymentStatusError={addPaymentStatusError}
					totalPayableAmount={totalPayableAmount || 0}
					totalPendingAmount={totalPendingAmount || 0}
				/>
			)}
		</StyledStack>
	);
}

export default PaymentStatus;
